import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Container, Button } from '../../styles/StyledComponents';

const HeroSection = styled.section`
  height: 100vh;
  min-height: 600px;
  display: flex;
  align-items: center;
  position: relative;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.secondary} 0%, #1a2040 100%);
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url('/images/hero-pattern.svg');
    background-size: cover;
    opacity: 0.1;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-height: 500px;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  color: ${({ theme }) => theme.colors.white};
  max-width: 700px;
`;

const HeroTitle = styled(motion.h1)`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  line-height: 1.2;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 2.8rem;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 2.2rem;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1.25rem;
  margin-bottom: 2.5rem;
  line-height: 1.6;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1.1rem;
  }
`;

const HeroButtonContainer = styled(motion.div)`
  display: flex;
  gap: 1rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const HeroGraphic = styled(motion.div)`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 40%;
  height: 80%;
  background-image: url('/images/hero-graphic.svg');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    opacity: 0.3;
    width: 60%;
  }
`;

const Hero = () => {
  return (
    <HeroSection>
      <Container>
        <HeroContent>
          <HeroTitle
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Not just strategy. Real systems. Built for impact.
          </HeroTitle>
          <HeroSubtitle
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            We don't just talk about AI. We help you find what to automate, build the system, launch it fast, and scale like crazy.
          </HeroSubtitle>
          <HeroButtonContainer
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Button 
              as="a" 
              href="https://cal.com/scott-laughlin-i6yml3/30min" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              Schedule Your Free Consultation
            </Button>
            <Button 
              as="a" 
              href="#benefits" 
              secondary
            >
              Learn More
            </Button>
          </HeroButtonContainer>
        </HeroContent>
      </Container>
      <HeroGraphic
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 0.6 }}
      />
    </HeroSection>
  );
};

export default Hero;
